import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { useRef, useState } from 'react';
import { IconButton, cn } from '@/components/ui';
import type { MenuGroup, MenuItem } from './types';
import { useSlidingBox } from './useSlidingBox';

/*
  SectionMenu — меню раздела платформы, уровень 2 (docs/foundation.md). Выезжает из-под главного
  меню и выглядит его продолжением: прилегает вплотную, свой фон на ступень светлее, справа рамка.
  Пункты со значками: у выбранного значок коралловый, мягкая подсветка переезжает с пружиной.
  Пункт с подпунктами уводит меню вглубь, сверху появляется «← назад».

  Два тона: тёмный — продолжение главного меню (по умолчанию), светлый — в цвет страницы.
*/

interface SectionMenuProps {
  title: string;
  groups: MenuGroup[];
  activeKey: string | null;
  onSelect: (item: MenuItem) => void;
  tone?: 'dark' | 'light';
}

interface Drill {
  key: string;
  parent: string;
  groups: MenuGroup[];
}

const ACTIVE = '[data-nav-active="true"]';

export function SectionMenu({ title, groups, activeKey, onSelect, tone = 'dark' }: SectionMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [drill, setDrill] = useState<Drill | null>(null);
  const dark = tone === 'dark';
  const { box, ready } = useSlidingBox(ref, ACTIVE, `${title}:${activeKey}:${drill?.key ?? ''}`);
  const shown = drill ? drill.groups : groups;

  return (
    <div
      className={cn(
        // Левые 16 px уходят под главное меню: фон виден за его скруглёнными углами.
        'flex h-full flex-col rounded-r-lg border-r pl-4',
        dark ? 'border-rail-line bg-rail-2 text-rail-fg' : 'border-line bg-canvas text-fg shadow-inner-card',
      )}
    >
      <div className="flex h-14 shrink-0 items-center justify-between gap-2 pr-3 pl-4">
        {drill ? (
          <button
            type="button"
            onClick={() => setDrill(null)}
            className={cn(
              '-ml-2 flex h-8 items-center gap-1 rounded-md px-2 text-sm font-medium transition-interactive',
              dark
                ? 'text-rail-fg-muted hover:bg-rail-hover hover:text-rail-fg'
                : 'text-fg-muted hover:bg-hover hover:text-fg',
            )}
          >
            <ChevronLeft className="size-3.5" />
            {drill.parent}
          </button>
        ) : (
          <h2 className="truncate text-base font-semibold tracking-tight">{title}</h2>
        )}
        <IconButton
          aria-label="Поиск в разделе"
          variant="ghost"
          size="sm"
          className={cn(dark && 'text-rail-fg-muted')}
        >
          <Search />
        </IconButton>
      </div>

      <div
        ref={ref}
        key={drill?.key ?? 'root'}
        className="relative min-h-0 flex-1 animate-fade-in overflow-y-auto px-2 pb-3"
      >
        <span
          aria-hidden
          data-ready={ready || undefined}
          className={cn(
            'pointer-events-none absolute rounded-md nav-slide',
            dark ? 'bg-rail-active' : 'bg-active',
          )}
          style={{
            top: box.top,
            left: box.left,
            width: box.width,
            height: box.height,
            opacity: box.visible ? 1 : 0,
          }}
        />
        {shown.map((group, gi) => (
          <div
            key={group.title ?? gi}
            className={cn(
              'relative flex flex-col gap-0.5',
              gi > 0 && 'mt-2 border-t pt-3',
              dark ? 'border-rail-line' : 'border-line',
            )}
          >
            {group.title && (
              <h3
                className={cn(
                  'mb-1 px-3 text-2xs font-semibold tracking-wider uppercase',
                  dark ? 'text-rail-fg-muted' : 'text-fg-subtle',
                )}
              >
                {group.title}
              </h3>
            )}
            {group.items.map((item) => (
              <Item
                key={item.key}
                item={item}
                active={item.key === activeKey}
                dark={dark}
                onSelect={(it) => {
                  if (it.children)
                    setDrill({ key: it.key, parent: drill?.parent ?? title, groups: it.children });
                  else onSelect(it);
                }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function Item({
  item,
  active,
  dark,
  onSelect,
}: {
  item: MenuItem;
  active: boolean;
  dark: boolean;
  onSelect: (item: MenuItem) => void;
}) {
  const Icon = item.icon;
  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      data-nav-active={active || undefined}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'group/item relative flex h-9 w-full items-center gap-2 rounded-md px-3 text-sm transition-interactive outline-none',
        active ? 'font-medium' : dark ? 'hover:bg-rail-hover' : 'hover:bg-hover',
        dark ? 'text-rail-fg' : 'text-fg',
      )}
    >
      {Icon && (
        <Icon
          className={cn(
            'size-4.5 shrink-0 transition-colors duration-normal',
            active ? 'text-brand' : dark ? 'text-rail-fg-muted' : 'text-fg-muted',
          )}
        />
      )}
      <span className="flex-1 truncate text-left">{item.label}</span>
      {item.count !== undefined && <Count item={item} dark={dark} />}
      {item.children && (
        <ChevronRight className="size-3.5 shrink-0 opacity-0 transition-opacity duration-fast group-hover/item:opacity-60" />
      )}
    </button>
  );
}

function Count({ item, dark }: { item: MenuItem; dark: boolean }) {
  const n = item.count ?? 0;
  const label = n > 99 ? '99+' : n;
  if (item.countTone && n > 0) {
    return (
      <span
        className={cn(
          'shrink-0 rounded-xs px-1.5 py-0.5 text-2xs leading-none font-semibold text-on-brand tabular-nums',
          item.countTone === 'danger' ? 'bg-danger' : 'bg-brand',
        )}
      >
        {label}
      </span>
    );
  }
  return (
    <span
      className={cn(
        'shrink-0 text-xs tabular-nums',
        n === 0 && 'opacity-50',
        dark ? 'text-rail-fg-muted' : 'text-fg-subtle',
      )}
    >
      {label}
    </span>
  );
}
